// NOTE: IMPORT OTHER CONSTANTS AND DEPENDENCIES
import React, { Suspense, lazy } from "react";
import Root from "../Layout/Root";
import { createBrowserRouter } from "react-router-dom";
import { ProtectedRoute } from "../components/protectedAndPublicRoute";
import LandingPage from "../pages/Landing/LandingPage";
import DashboardRoot from "../Layout/DashboardRoot";

// NOTE: LAZY IMPORT ALL INTERNAL APP PAGES (Prefetched in background after landing page paints)
const Home = lazy(() => import(/* webpackPrefetch: true */ "../pages/Home/Home"));
const Charts = lazy(() => import(/* webpackPrefetch: true */ "../pages/Charts/Charts"));
const Releases = lazy(() => import(/* webpackPrefetch: true */ "../pages/Releases/Releases"));
const Exclusives = lazy(() => import(/* webpackPrefetch: true */ "../pages/Exclusives/Exclusives"));
const Downloads = lazy(() => import(/* webpackPrefetch: true */ "../pages/Downloads/Downloads"));
const Favorites = lazy(() => import(/* webpackPrefetch: true */ "../pages/Favorites/Favorites"));
const Remixers = lazy(() => import(/* webpackPrefetch: true */ "../pages/Remixers/Remixers"));
const Playlist = lazy(() => import(/* webpackPrefetch: true */ "../pages/Playlist/Playlist"));
const Overview = lazy(() => import(/* webpackPrefetch: true */ "../pages/Playlist/Overview"));
const ProducerPacks = lazy(() => import(/* webpackPrefetch: true */ "../pages/ProducerPacks/ProducerPacks"));
const ProducerPackOverview = lazy(() => import(/* webpackPrefetch: true */ "../pages/ProducerPacks/ProducerPackOverview"));
const ArtistOverview = lazy(() => import(/* webpackPrefetch: true */ "../components/Common/ArtistOverview"));
const TrackOverview = lazy(() => import(/* webpackPrefetch: true */ "../components/Track/TrackOverview"));
const SearchResults = lazy(() => import(/* webpackPrefetch: true */ "../components/Common/SearchResults"));
const RemixerOverview = lazy(() => import(/* webpackPrefetch: true */ "../components/Common/RemixerOverview"));
const TracksByTag = lazy(() => import(/* webpackPrefetch: true */ "../pages/TacksByTag/tracksByTag"));

// NOTE: LAZY IMPORT HEAVY & ISOLATED PAGES
const SignIn = lazy(() => import("../pages/Login/SignIn"));
const SignUp = lazy(() => import("../pages/Login/SignUp"));
const ForgotPassword = lazy(() => import("../pages/Login/ForgotPassword"));
const SetNewPassword = lazy(() => import("../pages/Login/SetNewPassword"));

const Dashboard = lazy(() => import("../pages/Dashboard/Dashboard"));
const Helpdesk = lazy(() => import("../pages/Dashboard/Helpdesk"));
const Subscription = lazy(() => import("../pages/Dashboard/Subscription"));
const Membership = lazy(() => import("../pages/Dashboard/Membership"));

const SubmitNewTrackPage = lazy(() => import("../pages/Landing/SubmitNewTrack"));
const Faq = lazy(() => import("../pages/Faq"));
const PrivacyPolicy = lazy(() => import("../pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("../pages/TermsOfUse"));

const ErrorPage = lazy(() => import("../error/Error"));
const ServerError = lazy(() => import("../error/ServerError"));
const GenericError = lazy(() => import("../error/GenericError"));

const PageFallback = () => <div className="min-h-screen w-full bg-[#000000]" />;

const withSuspense = (Component) => (
  <Suspense fallback={<PageFallback />}>
    <Component />
  </Suspense>
);

const router = createBrowserRouter([
  {
    path: "/",
    element: <LandingPage />,
  },
  {
    path: "/upload-form",
    element: withSuspense(SubmitNewTrackPage),
  },
  {
    path: "/",
    element: <Root />,
    children: [
      // PUBLIC LEGAL & INFO ROUTES
      {
        path: "/faq",
        element: withSuspense(Faq),
      },
      {
        path: "/privacy-policy",
        element: withSuspense(PrivacyPolicy),
      },
      {
        path: "/terms",
        element: withSuspense(TermsOfUse),
      },

      // CORE AUTHENTICATED APP ROUTES
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: "/",
            element: withSuspense(Home),
          },
          {
            path: "/home",
            element: withSuspense(Home),
          },
          {
            path: "/releases",
            element: withSuspense(Releases),
          },
          {
            path: "/charts",
            element: withSuspense(Charts),
          },
          {
            path: "/exclusives",
            element: withSuspense(Exclusives),
          },
          {
            path: "/downloads",
            element: withSuspense(Downloads),
          },
          {
            path: "/favorites",
            element: withSuspense(Favorites),
          },
          {
            path: "/remixers",
            element: withSuspense(Remixers),
          },
          {
            path: "/playlist",
            element: withSuspense(Playlist),
          },
          {
            path: "/playlist/:playlistId",
            element: withSuspense(Overview),
          },
          {
            path: "/producer-packs",
            element: withSuspense(ProducerPacks),
          },
          {
            path: "/producer-packs/:packId",
            element: withSuspense(ProducerPackOverview),
          },
          {
            path: "/artist/:artistName",
            element: withSuspense(ArtistOverview),
          },
          {
            path: "/track/:trackId",
            element: withSuspense(TrackOverview),
          },
          {
            path: "/search",
            element: withSuspense(SearchResults),
          },
          {
            path: "/remixers/:remixerName",
            element: withSuspense(RemixerOverview),
          },
          {
            path: "/tags/:tagId",
            element: withSuspense(TracksByTag),
          },
        ],
      },
    ],
  },

  // DASHBOARD & BILLING ROUTES
  {
    path: "/",
    element: <DashboardRoot />,
    children: [
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: "/dashboard",
            element: withSuspense(Dashboard),
          },
          {
            path: "/helpdesk",
            element: withSuspense(Helpdesk),
          },
          {
            path: "/subscription",
            element: withSuspense(Subscription),
          },
          {
            path: "/membership",
            element: withSuspense(Membership),
          },
        ],
      },
    ],
  },

  // AUTH ROUTES
  {
    path: "/signin",
    element: withSuspense(SignIn),
  },
  {
    path: "/signup",
    element: withSuspense(SignUp),
  },
  {
    path: "/forgot-password",
    element: withSuspense(ForgotPassword),
  },
  {
    path: "/reset-password",
    element: withSuspense(SetNewPassword),
  },

  // ERROR ROUTES
  {
    path: "/server-error",
    element: withSuspense(ServerError),
  },
  {
    path: "*",
    element: withSuspense(ErrorPage),
  },
  {
    path: "/error",
    element: withSuspense(GenericError),
  },
]);

export default router;
